// @owners { team: patients }
import { Button, Description, ListItem } from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import React from 'react';
import { TypescriptUtils } from '~shared/TypescriptUtils';
import { selectUser } from '~shared/actions/ui/users';
import { PRONOUNS } from '~shared/constants';
import { normalizeDateString } from '~shared/helpers/date';
import { ORIGIN_NAMES } from '~shared/lib/analytics/src/getOrigin';
import { useDispatchShared } from '~shared/store';
import { type User } from '~shared/types';

type Props = {
  user: User;
};

export const PersonalInfo = ({ user }: Props) => {
  const dispatch = useDispatchShared();
  const { navigate } = useNavigation();
  const hasPhotoID = user.first_name && user.last_name && user.photo_id_url;

  const handleUploadPhotoID = () => {
    dispatch(selectUser(user.id));
    navigate('RouteCheckoutPhotoIdUpload', { origin: ORIGIN_NAMES.PROFILE });
  };

  return (
    <>
      <ListItem
        title="First & last name"
        descriptions={
          <Description>
            {[user.first_name, user.last_name].filter(TypescriptUtils.isPresent).join(' ') || 'Full name'}
          </Description>
        }
      />
      {user.preferred_first_name ? (
        <ListItem
          title="Preferred name"
          descriptions={<Description>{user.preferred_first_name || 'Unspecified'}</Description>}
        />
      ) : null}
      <ListItem
        title="Date of birth"
        descriptions={<Description>{normalizeDateString(user.date_of_birth || '') || 'Date of birth'}</Description>}
      />
      <ListItem
        title="Sex"
        descriptions={<Description>{user.gender || 'Unspecified'}</Description>}
      />
      {user.preferred_pronoun ? (
        <ListItem
          title="Pronouns"
          descriptions={<Description>{PRONOUNS[user.preferred_pronoun] || 'Unspecified'}</Description>}
        />
      ) : null}
      <ListItem
        title="Government-issued photo ID"
        BottomButton={
          <Button
            width="inline"
            small
            type="secondary"
            label={hasPhotoID ? 'Edit photo ID' : 'Upload photo ID'}
            onPress={handleUploadPhotoID}
          />
        }
      />
    </>
  );
};
